import React, { useState, useEffect } from 'react';
import { Input, Select, Icon, Button } from 'antd';
import style from '../solo.module.sass';
import StringIcon from '../../../../../assets/svg/solo/s.svg';
import BooleanIcon from '../../../../../assets/svg/solo/b.svg';
import VariableAcl from './VariableAcl';
import emptyVariableAclIcon from '../../../../../assets/svg/solo/variableAclNotConfigured.svg';
import variableAclIcon from "../../../../../assets/svg/solo/variableAclConfigured.svg"

type Props = {
	lock: any;
	nameCheck: any;
	widgetDrawer: any;
	nodeDetail: any;
	elements: any;
	setWidgetDrawer: any;
	onUpdateProperties: any;
	showVariableAcl: any;
	setShowVariableAcl: any;
	setAclIcon: any;
};

type LiveAgentSchema = {
	name: string;
	nextNode: string;
	params: Record<string, Param>;
};

type Param = {
	valueInfo: Record<string, any>;
	value: string;
	type: 'String' | 'Boolean';
};

const { Option } = Select;

const PRIORITIES = [
	'Highest',
	'Higher',
	'High',
	'Medium',
	'Low',
	'Lower',
	'Lowest',
];

export default function LiveAgent(props: Props) {
	const {
		elements,
		nodeDetail,
		showVariableAcl,
		setShowVariableAcl,
		setAclIcon,
	} = props;
	const [liveAgent, setLiveAgent] = useState<LiveAgentSchema>({
		name: '',
		nextNode: '',
		params: {},
	});
	const [error, setError] = useState({
		name: '',
	});

	const [variableAcl, setVariableAcl] = useState({});
	const [setupActiveTab, setSetupActiveTab] = useState('freeze');
	const [teardownActiveTab, setTeardownActiveTab] = useState('freeze');

	const existingNames: string[] = elements
		.filter((el: any) => el.id !== nodeDetail.id)
		.map((el: any) => el.data?.propertyDetails?.name)
		.filter((el: any) => el);

	const hasError = !Object.values(error).every((el) => el === '');

	const nodes: any[] = [];
	elements.forEach((e: any) => {
		if (
			e.data &&
			e.data.type &&
			e.data.type !== 'start' &&
			e.data.type !== 'error' &&
			e.id !== nodeDetail.id
		) {
			nodes.push(e);
		}
	});

	function updateStateByKey(key: keyof LiveAgentSchema, value: any) {
		setLiveAgent((prev) => ({
			...prev,
			[key]: value,
		}));
	}

	function updateProperties() {
		props.onUpdateProperties({
			nodeId: props.nodeDetail.id,
			idChange: true,
			variableAcl,
			...liveAgent,
		});
		props.setWidgetDrawer(false);
		setLiveAgent({
			name: '',
			nextNode: '',
			params: {},
		});
	}

	function validateAPIParams(params: LiveAgentSchema['params']) {
		const result: LiveAgentSchema['params'] = {};
		Object.entries(params).forEach(([key, value]) => {
			if (
				(value.type === 'String' || value.type === 'Boolean') &&
				typeof value.value === 'string'
			) {
				result[key] = value;
			}
		});
		return result;
	}

	useEffect(() => {
		if (props.widgetDrawer) {
			setSetupActiveTab('freeze');
			setTeardownActiveTab('freeze');
			setShowVariableAcl(false);
			setError({
				name: '',
			});
			const propertyDetails = nodeDetail.data.propertyDetails ?? {};
			const aclSetup = propertyDetails.variableAcl?.setup ?? {}
			const aclTeardown = propertyDetails.variableAcl?.teardown ?? {}
			if (
				aclSetup.freeze ||
				aclSetup.unfreeze ||
				aclSetup.hide ||
				aclSetup.unhide ||
				aclTeardown.freeze ||
				aclTeardown.unfreeze ||
				aclTeardown.hide ||
				aclTeardown.unhide
			) {
				setAclIcon(variableAclIcon);
			} else {
				setAclIcon(emptyVariableAclIcon);
			}
			if(propertyDetails.variableAcl) {
			  setVariableAcl(propertyDetails.variableAcl)
			}
			const nextNode = props.elements.find((el: any) => {
				return el.source === nodeDetail.id;
			});

			setLiveAgent({
				name: propertyDetails.name ?? props.nodeDetail.id,
				nextNode: nextNode?.target ?? propertyDetails.nextNode ?? '',
				params: validateAPIParams(propertyDetails.params ?? {}),
			});
		}
	}, [props.widgetDrawer, nodeDetail.data.propertyDetails]);

	// update next node when changes happen in the canvas
	useEffect(() => {
		const findNextNode = elements?.find((el: any) => {
			return el?.source === nodeDetail.id;
		});
		if (findNextNode) {
			updateStateByKey('nextNode', findNextNode.target);
		}
	}, [elements]);

	if (showVariableAcl) {
		return (
			<VariableAcl
				variableAcl={variableAcl}
				setVariableAcl={setVariableAcl}
				teardownActiveTab={teardownActiveTab}
				setTeardownActiveTab={setTeardownActiveTab}
				setupActiveTab={setupActiveTab}
				setSetupActiveTab={setSetupActiveTab}
				setShowVariableAcl={setShowVariableAcl}
				name={liveAgent.name}
				lock={props.lock}
			></VariableAcl>
		);
	}

	return (
		<div style={{ height: '100%' }}>
			<div className={style.conditionInput}>
				<div style={{ width: '100%' }}>
					<div
						className={style.label}
						style={{ color: '#2A2A2A', fontSize: 14 }}
					>
						<label>Name</label>
					</div>
					<Input
						disabled={props.lock}
						style={{
							width: '100%',
							height: '44px',
							color: '#000610',
							fontSize: 15,
							border: props.nameCheck
								? '1px solid #FF0000'
								: '1px solid #909BAD',
						}}
						value={liveAgent.name}
						placeholder="Enter Node Name"
						onChange={(e) => {
							const value = e.target.value;
							setError((err) => ({
								...err,
								name: value.trim() === '' ? "Name shouldn't be empty" : '',
							}));
							if (existingNames.includes(value.trim())) {
								setError((err) => ({ ...err, name: 'Name already exists' }));
							}
							updateStateByKey('name', value);
						}}
					/>
					{error.name && (
						<p
							style={{
								color: '#FF0000',
								position: 'relative',
								textAlign: 'center',
								margin: '5px',
								fontSize: 14,
							}}
						>
							{error.name}
						</p>
					)}
				</div>
			</div>
			<div
				style={{
					borderBottom: '1px solid rgb(202, 213, 233)',
					margin: '20px 18px 20px 18px',
				}}
			></div>
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					width: '100%',
					padding: '0px 15px',
				}}
			>
				<div
					className={style.label}
					style={{ color: '#2A2A2A', marginTop: 0, fontSize: 14 }}
				>
					<label>Next Node</label>
				</div>
				<Select
					showSearch
					onChange={(e: any) => updateStateByKey('nextNode', e)}
					suffixIcon={
						<Icon style={{ fontSize: '20px', margin: -5 }} type="caret-down" />
					}
					disabled={props.lock}
					value={liveAgent.nextNode || undefined}
					placeholder="Select the next node"
					optionLabelProp="label"
					style={{
						width: '100%',
						borderRadius: 8,
						color: '#000610',
						fontSize: 15,
					}}
					mode="default"
					size="large"
					filterOption={(input, option: any) => {
						return option && option.props
							? option.props.label
									.toString()
									.toLowerCase()
									.indexOf(input.toLowerCase()) >= 0
							: false;
					}}
				>
					{nodes &&
						nodes.map((el: any) => {
							return (
								<Option
									key={el.id}
									value={el.id}
									label={
										(el &&
											el.data &&
											el.data.propertyDetails &&
											el.data.propertyDetails &&
											el.data.propertyDetails.name) ||
										el.id
									}
								>
									<div
										style={{
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'center',
										}}
									>
										<div style={{ display: 'flex', flexDirection: 'row' }}>
											<img alt="icons" src={el.data.icon} />
											<p
												style={{
													marginLeft: '8px',
													marginTop: '14px',
													color:
														el.id === liveAgent.nextNode
															? '#0F66C8'
															: '#000610',
												}}
											>
												{(el &&
													el.data &&
													el.data.propertyDetails &&
													el.data.propertyDetails &&
													el.data.propertyDetails.name) ||
													el.id}
											</p>
										</div>
										{el.id === liveAgent.nextNode && (
											<Icon type="check" style={{ color: '#0F66C8' }} />
										)}
									</div>
								</Option>
							);
						})}
				</Select>
			</div>
			<div style={{ margin: '20px 20px 12px 20px' }}>
				<p style={{ color: '#657695', fontSize: 14 }}>Parameters</p>
				<div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
					<Parameter
						label="groupId"
						type="String"
						lock={props.lock}
						updateStateByKey={updateStateByKey}
						liveAgent={liveAgent}
					/>
					<Parameter
						label="groupIds"
						type="String"
						lock={props.lock}
						liveAgent={liveAgent}
						updateStateByKey={updateStateByKey}
					/>
					<Parameter
						label="priority"
						type="String"
						lock={props.lock}
						liveAgent={liveAgent}
						updateStateByKey={updateStateByKey}
					/>
					<Parameter
						label="assistFlow"
						type="Boolean"
						lock={props.lock}
						liveAgent={liveAgent}
						updateStateByKey={updateStateByKey}
					/>
				</div>
			</div>

			<div className={style.footer}>
				{!props.lock && (
					<Button
						disabled={hasError}
						style={{
							backgroundColor: '#51808C',
							color: '#FFFFFF',
							width: '100%',
							height: 50,
						}}
						onClick={() => {
							updateProperties();
						}}
					>
						APPLY
					</Button>
				)}
			</div>
		</div>
	);
}

type ParameterProps = {
	liveAgent: LiveAgentSchema;
	updateStateByKey: (key: keyof LiveAgentSchema, value: any) => void;
	label: string;
	lock: boolean;
	type: Param['type'];
};

function Parameter(props: ParameterProps) {
	const { label, type, liveAgent, updateStateByKey } = props;
	return (
		<div style={{ display: 'flex', gap: 13 }}>
			<div
				style={{
					height: 43,
					paddingLeft: 10,
					margin: 0,
					display: 'flex',
					fontSize: 15,
					alignItems: 'center',
					border: '1px solid #909BAD',
					color: '#000610',
					flex: 1,
					borderRadius: 5,
				}}
			>
				{label}
			</div>
			<div style={{ position: 'relative', flex: 1 }}>
				<img
					style={{ position: 'absolute', top: 0, left: 0, zIndex: 4 }}
					src={type === 'String' ? StringIcon : BooleanIcon}
					alt="parameter type"
				/>
				{label !== 'priority' ? (
					<Input
						disabled={props.lock}
						style={{
							height: 43,
							width: '100%',
							color: '#000610',
							border: '1px solid #909BAD',
							paddingLeft: 40,
							borderRadius: 5,
						}}
						value={liveAgent.params[label]?.value}
						placeholder="value"
						onChange={(e) => {
							const value = e.target.value;
							updateStateByKey('params', {
								...liveAgent.params,
								[label]: {
									valueInfo: {},
									type,
									value,
								},
							});
						}}
					/>
				) : (
					<Select
						allowClear
						className="liveAgentSelect"
						style={{ width: '100%', height: 43, borderRadius: 5 }}
						disabled={props.lock}
						placeholder="value"
						value={liveAgent.params[label]?.value}
						onChange={(e: any) => {
							updateStateByKey('params', {
								...liveAgent.params,
								priority: {
									valueInfo: {},
									type,
									value: e,
								},
							});
						}}
					>
						{PRIORITIES.map((priority) => {
							return (
								<Option key={priority} value={priority}>
									{priority}
								</Option>
							);
						})}
					</Select>
				)}
			</div>
		</div>
	);
}
